import React, { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Typography } from '@mui/material';

import { gray100, primaryButtonBackgroundColor, gray40, white } from '../../../../constants/colors';
import { companyEditSchema } from '../../../../validation/company';
import type { CompanyEditFormType } from '../../../../types/company';

import CustomTextfield from '../../../uiParts/form/CustomTextfield';
import CustomSnackbar from '../../../uiParts/form/CustomSnackbar';
import CustomLoading from '../../../uiParts/form/CustomLoading';
import { AuthContext } from '../../../../contexts/auth';
import { useAuthRedirect } from '../../../../hooks/useAuthRedirect';
import MetaTitle from '../../../utils/MetaTitle';
import SideBar from '../../../uiParts/menu/SideBar';
import { usePageLayout } from '../../../../hooks/usePageLayout';
import { useProxyAccessControl } from '../../../../hooks/useProxyAccessControl';
import { getCompany } from '../../../../api/firebase/firestore/company/read';
import { updateCompany } from '../../../../api/firebase/firestore/company/update';

const styles = {
  pageContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    width: '100%',
    boxSizing: 'border-box',
  },
  modalContainer: {
    backgroundColor: white,
    borderRadius: '4px',
    border: `1px solid ${gray40}`,
    padding: '40px',
    width: '480px',
    boxSizing: 'border-box',
    position: 'relative',
    '@media screen and (max-width: 960px)': {
      padding: '32px 16px',
      width: '100%',
      maxWidth: '480px',
    },
  },
  modalHeader: {
    marginBottom: '24px',
    '@media screen and (max-width: 960px)': {
      marginBottom: '16px',
    },
  },
  modalTitle: {
    color: gray100,
    fontSize: '22px',
    fontWeight: '500',
    textAlign: 'left',
    '@media screen and (max-width: 960px)': {
      fontSize: '20px',
    },
  },
  submitButton: {
    width: '400px',
    height: '44px',
    fontSize: '16px',
    fontWeight: '700',
    marginTop: '32px',
    '@media screen and (max-width: 960px)': {
      marginTop: '20px',
      width: '-webkit-fill-available',
    },
  },
};

const CompanyEdit: React.FC = (): JSX.Element => {
  const [errorText, setErrorText] = useState('');
  const [open, setOpen] = useState(false);
  const [firstSubmit, setFirstSubmit] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const { user, isLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  // 未認証ならログイン画面へ退避する（元URLは ?redirect= で引き継ぐ。#3203）
  useAuthRedirect();
  const [loading, setLoading] = useState<boolean>(true);
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();

  // 代理モード中はアクセス禁止
  useProxyAccessControl();

  const {
    control,
    watch,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<CompanyEditFormType>({
    resolver: yupResolver(companyEditSchema),
    defaultValues: {
      companyName: '',
      phoneNumber: '',
    },
  });

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('companyName') === '' ||
        watch('phoneNumber') === '' ||
        Boolean(errors.companyName) ||
        Boolean(errors.phoneNumber)
      );
    }
    return false;
  };

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);

      if (!user?.company_id || !user?.uid) {
        setErrorText('ユーザー情報の取得に失敗しました。');
        setOpen(true);
        return;
      }

      const companyName = watch('companyName');
      const phoneNumber = watch('phoneNumber');

      await updateCompany(user.company_id, companyName, phoneNumber, user.uid);

      navigate('/company/edit/complete', {
        state: { fromCompanyEdit: true },
      });
    } catch (error: unknown) {
      setErrorText('会社情報の更新に失敗しました。');
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchCompanyData = async (): Promise<void> => {
      // 認証ロード中は何もしない
      if (isLoading) return;

      if (user === null) {
        return;
      }

      // company_idがない場合、または既にデータ取得済みの場合は何もしない
      if (!user.company_id || dataFetched) return;

      try {
        const companyData = await getCompany(user.company_id);

        if (companyData) {
          setValue('companyName', companyData.company_name || '');
          setValue('phoneNumber', companyData.phone_number || '');
          setDataFetched(true);
        }
      } catch (error: unknown) {
        setErrorText('会社情報の取得に失敗しました。');
        setOpen(true);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, [isLoading, user, navigate, setValue, dataFetched]);

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle />
      <CustomLoading loading={loading} />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>

      <Box
        sx={[
          layoutStyles.pageContent,
          sideBarCollapsed && layoutStyles.pageContentCollapsed,
          layoutStyles.pageContentNoFooter,
        ]}
      >
        <Box sx={[styles.pageContainer, layoutStyles.fullHeightContent]}>
          <Box sx={styles.modalContainer}>
            <Box sx={styles.modalHeader}>
              <Typography sx={styles.modalTitle}>会社情報編集</Typography>
            </Box>

            <Controller
              render={({ field }): JSX.Element => (
                <CustomTextfield
                  {...field}
                  error={Boolean(errors.companyName)}
                  formMb="24px"
                  helperText={errors.companyName?.message}
                  labelText="会社名"
                  placeholder="株式会社〇〇〇"
                  requiredText="必須"
                  width="100%"
                />
              )}
              control={control}
              name="companyName"
            />

            <Controller
              render={({ field }): JSX.Element => (
                <CustomTextfield
                  {...field}
                  error={Boolean(errors.phoneNumber)}
                  formMb="0px"
                  helperText={errors.phoneNumber?.message}
                  labelText="電話番号"
                  placeholder="000-0000-0000"
                  requiredText="必須"
                  width="100%"
                />
              )}
              control={control}
              name="phoneNumber"
            />

            <Button
              disabled={buttonDisabledFlag()}
              onClick={async (): Promise<void> => {
                setFirstSubmit(true);
                await handleSubmit(async () => {
                  onSubmit();
                })();
              }}
              sx={[primaryButtonBackgroundColor, styles.submitButton]}
              variant="contained"
            >
              変更する
            </Button>
          </Box>
        </Box>
      </Box>

      <CustomSnackbar open={open} onClose={(): void => setOpen(false)} text={errorText} />
    </Box>
  );
};

export default CompanyEdit;
